import {
  ADHD_QUESTIONNAIRE_DRAFT_KEY_PREFIX,
  ADHD_YOUNG_QUESTIONNAIRE_DRAFT_KEY_PREFIX,
  YOUNG_QUESTIONNAIRE_TEMPLATE_ID,
} from '../data/constants';
import { safeGetLocalStorage } from './browser-storage';

export const DRAFT_EXPIRY_MS = 15 * 24 * 60 * 60 * 1000; // 15 days

export interface QuestionnaireDraft {
  answers: Record<string, unknown>;
  questionIndex: number;
  savedAt: number;
  expiresAt: number;
  explicitlySaved: boolean;
}

interface SaveQuestionnaireDraftParams {
  answers: Record<string, unknown>;
  questionIndex: number;
  explicitlySaved: boolean;
}

const draftKeyPrefix = (templateId: string): string => (
  templateId === YOUNG_QUESTIONNAIRE_TEMPLATE_ID
    ? ADHD_YOUNG_QUESTIONNAIRE_DRAFT_KEY_PREFIX
    : ADHD_QUESTIONNAIRE_DRAFT_KEY_PREFIX
);

const draftKey = (templateId: string): string => `${draftKeyPrefix(templateId)}:${templateId}`;

export const clearQuestionnaireDraft = (templateId: string): void => {
  const storage = safeGetLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(draftKey(templateId));
  } catch {
    // ignore
  }
};

export const loadQuestionnaireDraft = (
  templateId: string,
): QuestionnaireDraft | null => {
  const storage = safeGetLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(draftKey(templateId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (
      typeof parsed.answers === 'object'
      && typeof parsed.questionIndex === 'number'
      && typeof parsed.savedAt === 'number'
      && typeof parsed.expiresAt === 'number'
      && typeof parsed.explicitlySaved === 'boolean'
    ) {
      return parsed as QuestionnaireDraft;
    }
    return null;
  } catch {
    return null;
  }
};

export const loadValidQuestionnaireDraft = (
  templateId: string,
): QuestionnaireDraft | null => {
  const draft = loadQuestionnaireDraft(templateId);
  if (!draft) return null;
  if (!draft.explicitlySaved) return null;
  if (Date.now() >= draft.expiresAt) {
    clearQuestionnaireDraft(templateId);
    return null;
  }
  return draft;
};

export const saveQuestionnaireDraft = (
  templateId: string,
  params: SaveQuestionnaireDraftParams,
): void => {
  const storage = safeGetLocalStorage();
  if (!storage) return;
  try {
    const now = Date.now();
    const draft: QuestionnaireDraft = {
      answers: params.answers,
      questionIndex: params.questionIndex,
      savedAt: now,
      expiresAt: now + DRAFT_EXPIRY_MS,
      explicitlySaved: params.explicitlySaved,
    };
    storage.setItem(draftKey(templateId), JSON.stringify(draft));
  } catch {
    // ignore quota errors
  }
};
